/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { AxisPosition, DataAxisProps } from '#utils/UNSAFE_visTypes/chart';
import { Dimension } from '#utils/UNSAFE_visTypes/common';
import { AXIS_DEFAULTS } from './axisDefaults';
import { VisTextStyle } from '#utils/UNSAFE_visUtils';
import type { Font, GetTextDimensionsType } from '#hooks/PRIVATE_useTextDimensions';

const AXIS_MAX_RATIO = 0.3;

/**
 * Returns resolved axis position.
 */
export function getAxesPosition(
  yAxis: DataAxisProps | undefined,
  y2Axis: DataAxisProps | undefined,
  isHoriz: boolean,
  isRtl: boolean
): {
  xAxisPosition: AxisPosition;
  yAxisPosition: AxisPosition;
  y2AxisPosition: AxisPosition;
} {
  let xAxisPosition, yAxisPosition: AxisPosition, y2AxisPosition: AxisPosition;

  if (isHoriz) {
    yAxisPosition = yAxis?.position === 'top' ? 'top' : 'bottom';
    y2AxisPosition = y2Axis?.position === 'bottom' ? 'bottom' : 'top';
    xAxisPosition = (isRtl ? 'right' : 'left') as AxisPosition;
  } else {
    xAxisPosition = 'bottom' as AxisPosition;
    const isYEnd = yAxis?.position === 'end';
    const isY2End = y2Axis?.position === 'end';
    yAxisPosition = isYEnd ? (isRtl ? 'left' : 'right') : isRtl ? 'right' : 'left';
    y2AxisPosition = isY2End ? (isRtl ? 'right' : 'left') : isRtl ? 'left' : 'right';
  }
  return { xAxisPosition, yAxisPosition, y2AxisPosition };
}

/**
 * Returns the tick label anchor position for axis.
 * @param position
 * @param isRtl
 */
export function getTickLabelAnchor(position: AxisPosition, isRtl: boolean, isGroupAxis?: boolean) {
  if (!isRtl) {
    return position === 'left' ? 'end' : position === 'right' ? 'start' : 'middle';
  }
  return position === 'left' || (isGroupAxis && position === 'right')
    ? 'start'
    : position === 'right'
    ? 'end'
    : 'middle';
}

/**
 * Returns the scale for the axis.
 * @param availSpace
 * @param axisViewportMin
 * @param axisViewportMax
 * @param position
 * @returns
 */
export function getAxisRange(
  availSpace: Dimension,
  position: AxisPosition,
  isRtl: boolean,
  isTimeAxis?: boolean
) {
  const { x, y, width, height } = availSpace;
  const y2 = y + height;
  const x2 = x + width;

  let rangeStart: number;
  let rangeEnd: number;

  if (position === 'left' || position === 'right') {
    rangeStart = isTimeAxis ? y : y2;
    rangeEnd = isTimeAxis ? y2 : y;
  } else {
    rangeStart = isRtl ? x2 : x;
    rangeEnd = isRtl ? x : x2;
  }

  return [rangeStart, rangeEnd];
}

/**
 * Returns the maximum possible size of the axis.
 * @param availSpace The available space.
 * @param isHoriz If the axis is horizontal.
 * @param axisSize The ratio of axis to the chart.
 * @returns
 */
export function getAxisMaxSize(
  availSpace: Dimension,
  isHoriz: boolean,
  axisSize?: number,
  axisMaxSize?: number
) {
  const size = Math.max(0, Math.min(1, getAxisSize(axisSize, axisMaxSize)));
  return (isHoriz ? availSpace.height : availSpace.width) * size;
}

/**
 * Returns the width of a group for given availableSpace, axisposition and axis extents.
 * @param availSpace The available space to render the axis
 * @param position The position of the axis
 * @param min The minimum value of axis coord
 * @param max The maximum value of axis coord
 * @returns
 */
export function getGroupWidth(
  availSpace: Dimension,
  position: AxisPosition,
  min: number,
  max: number
) {
  const isHoriz = position === 'top' || position === 'bottom';
  if (isHoriz) {
    return availSpace.width / (max - min);
  } else {
    return availSpace.height / (max - min);
  }
}

/**
 * Returns the num of labels to skip safely.
 * @param axisStepWidth
 * @param tickLabelStyle
 * @param getTextDimensions
 * @returns
 */
export function getSafeSkips(
  axisStepWidth: number,
  tickLabelStyle: VisTextStyle,
  getTextDimensions?: GetTextDimensionsType
) {
  if (!getTextDimensions) {
    return 0;
  }
  const textDims = getTextDimensions('MW', tickLabelStyle as Font);
  const gaps = AXIS_DEFAULTS.labelGap;
  return Math.floor((0.5 * textDims.width + gaps) / (2 * axisStepWidth));
}

export function getGroupWidthRatios(barWidths: number[], barGapRatio: number, numGroups: number) {
  let barWidthSum, gapWidthSum: number, groupWidths;
  if (barWidths.length === 0) {
    barWidthSum = numGroups;

    gapWidthSum = (barWidthSum * barGapRatio) / (1 - barGapRatio);

    // no need for group widths if z is constant
    groupWidths = null;
  } else {
    barWidthSum = barWidths?.reduce((prev, cur) => prev + cur, 0);

    // The gap size is the same for all groups, regardless of the bar width.
    gapWidthSum = (barWidthSum * barGapRatio) / (1 - barGapRatio);
    groupWidths = barWidths.map((_barWidth) => {
      // divide the gaps evenly
      return _barWidth + gapWidthSum / numGroups;
    });
  }

  const averageGroupZ = (barWidthSum + gapWidthSum) / numGroups;

  return { groupWidths, averageGroupZ };
}

function getAxisSize(axisSize?: number, axisMaxSize?: number) {
  if (axisSize && axisMaxSize) {
    return axisSize < axisMaxSize ? axisSize : axisMaxSize;
  }
  return axisSize ?? axisMaxSize ?? AXIS_MAX_RATIO;
}
